import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Spinner, CheckCircle, XCircle, PencilSimple, UsersThree, Buildings, CalendarBlank,
} from '@phosphor-icons/react';
import logisticsApi from '../../lib/logisticsApi';

const EXERCISE_TYPES = [
  { value: 'tabletop', label: 'Tabletop' },
  { value: 'field', label: 'Field' },
  { value: 'functional', label: 'Functional' },
  { value: 'full-scale', label: 'Full-Scale' },
];

const EditExerciseDrawer = ({ open, exercise, onClose, onUpdated }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [subCompany, setSubCompany] = useState('');
  const [exerciseType, setExerciseType] = useState('');
  const [description, setDescription] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [hubUsers, setHubUsers] = useState([]);
  const [assignedSimcels, setAssignedSimcels] = useState([]);
  const [assignedObservers, setAssignedObservers] = useState([]);
  const [assignedParticipants, setAssignedParticipants] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open || !exercise) return;
    setStep(1);
    setName(exercise.name || '');
    setCompany(exercise.company || '');
    setSubCompany(exercise.sub_company || '');
    setExerciseType(exercise.exercise_type || '');
    setDescription(exercise.description || '');
    setStartAt(exercise.start_at ? exercise.start_at.slice(0, 16) : '');
    setEndAt(exercise.end_at ? exercise.end_at.slice(0, 16) : '');
    setAssignedSimcels(exercise.assigned_simcels || []);
    setAssignedObservers(exercise.assigned_observers || []);
    setAssignedParticipants(exercise.assigned_participants || []);
    setError(null);
    setSuccess(false);
    (async () => {
      try {
        const users = await logisticsApi.hubUsers();
        setHubUsers(users);
      } catch { /* ignore */ }
    })();
  }, [open, exercise]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await logisticsApi.updateExercise(exercise.logistics_exercise_id, {
        name: name.trim(),
        company: company.trim() || null,
        sub_company: subCompany.trim() || null,
        exercise_type: exerciseType || null,
        description: description.trim() || null,
        start_at: startAt || null,
        end_at: endAt || null,
        assigned_simcels: assignedSimcels,
        assigned_observers: assignedObservers,
        assigned_participants: assignedParticipants,
      });
      setSuccess(true);
      setTimeout(() => onUpdated?.(), 800);
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUser = (list, setList, userId) => {
    setList(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(0,0,0,0.5)' }}>
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="w-full max-w-lg bg-[var(--ls-bg)] h-full overflow-y-auto flex flex-col"
          style={{ borderLeft: '1px solid var(--ls-border)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--ls-border)' }}>
            <div className="flex items-center gap-2">
              <PencilSimple size={20} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
              <h2 className="ls-heading text-lg font-bold">Edit Exercise</h2>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--ls-elevated)', color: 'var(--ls-primary)' }}>
                Step {step}/2
              </span>
            </div>
            <button onClick={onClose} className="ls-btn-ghost !p-1.5"><X size={18} /></button>
          </div>

          {success ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8">
              <CheckCircle size={56} weight="duotone" style={{ color: 'var(--ls-success)' }} className="mb-4" />
              <h3 className="ls-heading text-xl font-bold mb-2">Exercise Updated</h3>
              <p className="text-sm" style={{ color: 'var(--ls-muted)' }}>{name}</p>
            </div>
          ) : (
            <div className="flex-1 p-5 overflow-y-auto">
              {step === 1 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <Buildings size={16} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
                    <span className="ls-overline">EXERCISE DETAILS</span>
                  </div>
                  <label className="block mb-3">
                    <span className="ls-overline text-xs mb-1 block">Exercise Name *</span>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} className="ls-input w-full" />
                  </label>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <label className="block">
                      <span className="ls-overline text-xs mb-1 block">Company</span>
                      <input type="text" value={company} onChange={e => setCompany(e.target.value)} className="ls-input w-full" />
                    </label>
                    <label className="block">
                      <span className="ls-overline text-xs mb-1 block">Sub-Company</span>
                      <input type="text" value={subCompany} onChange={e => setSubCompany(e.target.value)} className="ls-input w-full" />
                    </label>
                  </div>
                  <label className="block mb-3">
                    <span className="ls-overline text-xs mb-1 block">Exercise Type</span>
                    <select value={exerciseType} onChange={e => setExerciseType(e.target.value)} className="ls-input w-full">
                      <option value="">Select type...</option>
                      {EXERCISE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                  <label className="block mb-3">
                    <span className="ls-overline text-xs mb-1 block">Description</span>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} className="ls-input w-full" rows={3} />
                  </label>
                  <div className="flex items-center gap-2 mb-3">
                    <CalendarBlank size={16} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
                    <span className="ls-overline">DATES</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="ls-overline text-xs mb-1 block">Start</span>
                      <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="ls-input w-full" />
                    </label>
                    <label className="block">
                      <span className="ls-overline text-xs mb-1 block">End</span>
                      <input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} className="ls-input w-full" />
                    </label>
                  </div>
                </>
              )}
              {step === 2 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <UsersThree size={16} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
                    <span className="ls-overline">ROLE ASSIGNMENTS</span>
                  </div>

                  {[
                    { label: 'SimCells', list: assignedSimcels, setList: setAssignedSimcels, desc: 'Full control within this exercise' },
                    { label: 'Observers', list: assignedObservers, setList: setAssignedObservers, desc: 'Read-only access' },
                    { label: 'Participants', list: assignedParticipants, setList: setAssignedParticipants, desc: 'Can spend and book' },
                  ].map(({ label, list, setList, desc }) => (
                    <div key={label} className="mb-5">
                      <div className="flex items-baseline justify-between mb-2">
                        <span className="ls-overline text-xs">{label} ({list.length})</span>
                        <span className="text-[10px]" style={{ color: 'var(--ls-muted)' }}>{desc}</span>
                      </div>
                      <div className="ls-surface p-3 max-h-[160px] overflow-y-auto space-y-1.5">
                        {hubUsers.length === 0 ? (
                          <p className="text-xs" style={{ color: 'var(--ls-muted)' }}>Loading users…</p>
                        ) : hubUsers.map(u => (
                          <label key={u.user_id} className="flex items-center gap-2 text-sm cursor-pointer py-1 px-1 rounded hover:bg-[var(--ls-accent)] transition-colors">
                            <input
                              type="checkbox"
                              checked={list.includes(u.user_id)}
                              onChange={() => toggleUser(list, setList, u.user_id)}
                              className="rounded"
                            />
                            <span style={{ color: 'var(--ls-secondary)' }}>{u.name}</span>
                            <span className="text-xs" style={{ color: 'var(--ls-muted)' }}>{u.email}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {error && (
                <p className="text-sm mt-4 flex items-center gap-1.5" style={{ color: 'var(--ls-danger)' }}>
                  <XCircle size={14} weight="fill" /> {error}
                </p>
              )}
            </div>
          )}

          {/* Footer */}
          {!success && (
            <div className="p-5 border-t flex justify-between" style={{ borderColor: 'var(--ls-border)' }}>
              {step > 1 ? (
                <button onClick={() => setStep(s => s - 1)} className="ls-btn-ghost text-sm">Back</button>
              ) : <div />}
              {step < 2 ? (
                <button onClick={() => setStep(2)} className="ls-btn-primary text-sm" disabled={!name.trim()}>
                  Next
                </button>
              ) : (
                <button onClick={handleSave} className="ls-btn-primary text-sm" disabled={submitting || !name.trim()}>
                  {submitting ? <Spinner size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EditExerciseDrawer;
